import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * Result of a successful upload. Mirrors the shape returned by
 * `@99designs/design-services-common`'s `UploadedFile`.
 */
export interface UploadedFileResult {
    id: string;
    name: string;
    originalFileSize: string;
    previewUrl: string;
    downloadUrl: string;
}

/**
 * Transport surface for file uploads consumed by the chat input. Kept narrow
 * so the lib does not depend on the identity context directly — the consumer
 * (`ChatBrief.tsx`) constructs a real uploader via `useFileUploader` and
 * forwards it; tests can hand in a no-op implementation.
 */
export interface FileUploaderService {
    uploadFiles: (files: File[]) => Promise<UploadedFileResult[]>;
    deleteFile: (uploadId: string) => Promise<void>;
    loading: boolean;
    error: Error | null;
}

const FileUploaderContext = createContext<FileUploaderService | null>(null);

export interface FileUploaderProviderProps {
    uploader: FileUploaderService;
    children: ReactNode;
}

/** Provides the MCP file uploader to the chat input subtree. */
export function FileUploaderProvider({ uploader, children }: FileUploaderProviderProps) {
    return <FileUploaderContext.Provider value={uploader}>{children}</FileUploaderContext.Provider>;
}

/**
 * Access the active uploader. Returns null when no provider is mounted so
 * the input can omit the attachment button rather than throw — important for
 * surfaces that render the chat without an authenticated identity.
 */
export function useFileUploaderService(): FileUploaderService | null {
    return useContext(FileUploaderContext);
}
